import { Divider, Gutter } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ProductCart, ProductCartTranslations } from '@cdaas/reusable-components-daas';
import { modalClose, modalShow, shopClearOrderOnBehalfUser, shopResetProduct } from '../../../../store/actions';

import { RootState } from '../../../../store/reducers';
import { LocalBundleEnvelopeWithChosenMainVariant } from '../../../../types';
import { LocalProductVariant } from '@cdaas/api-clients/dist/product-service-openapi';

interface CheckoutProductCartProps {
  isMobile?: boolean;
}

const CheckoutProductCart = ({ isMobile }: CheckoutProductCartProps) => {
  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const orderResponse = useSelector((state: RootState) => state.shop.orderResponse);
  const product = useSelector((state: RootState) => state.shop.cart);
  const user = useSelector((state: RootState) => state.authentication.user);

  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useTranslation('shop');

  const productCartTranslations: ProductCartTranslations = {
    yourCart: t('checkout.mainPage.cart'),
    emptyCart: t('checkout.mainPage.emptyCart'),
    emptyCartHelp: t('checkout.mainPage.emptyCartHelp'),
    availabilityAvailable: t('checkout.mainPage.available'),
    availabilityNotAvailable: t('checkout.mainPage.notAvailable'),
    availabilityReadyToShip: t('checkout.mainPage.readyToShip'),
    availabilityFewLeft: t('checkout.mainPage.fewLeft'),
    availabilityNotReadyToShip: t('checkout.mainPage.notReadyToShip'),
    totalMonthlyPrice: t('checkout.mainPage.totalMonthlyPrice'),
    vat: t('checkout.mainPage.vat'),
    includingVat: t('checkout.mainPage.includingVat'),
    excludingVat: t('checkout.mainPage.excludingVat'),
    paymentSingle: t('checkout.mainPage.paymentSingle'),
    paymentMulti: t('checkout.mainPage.paymentMulti'),
    perMonth: t('checkout.mainPage.perMonth'),
    cartEmpty: t('checkout.mainPage.cartEmpty'),
    currency: user?.organization?.locale.currency.toUpperCase() ?? '',
    yourAllowance: t(orderingOnBehalfUserData.status ? 'checkout.mainPage.userAllowance' : 'checkout.mainPage.yourAllowance'),
    yourAllowanceExplanation: t(
      orderingOnBehalfUserData.status ? 'checkout.mainPage.userAllowanceExplanation' : 'checkout.mainPage.yourAllowanceExplanation',
    ),
    salaryDeductionEstimate: t('checkout.mainPage.salaryDeductionEstimate'),
    totalPrice: t('checkout.mainPage.totalPrice'),
    months: t('checkout.mainPage.months'),
    monthlySalaryDeductionExplanation: t(
      orderingOnBehalfUserData.status
        ? 'checkout.mainPage.monthlySalaryDeductionExplanationOob'
        : 'checkout.mainPage.monthlySalaryDeductionExplanation',
    ),
    oneTimeSalaryDeductionExplanation: t(
      orderingOnBehalfUserData.status
        ? 'checkout.mainPage.oneTimeSalaryDeductionExplanationOob'
        : 'checkout.mainPage.oneTimeSalaryDeductionExplanation',
    ),
  };

  const handleEmptyCartClick = (): void => {
    dispatch(
      modalShow(
        t('emptyCartModal.title'),
        t('emptyCartModal.acceptButton'),
        t('emptyCartModal.cancelButton'),
        () => {
          dispatch(shopResetProduct());
          dispatch(shopClearOrderOnBehalfUser());
          dispatch(modalClose());
          history.push('/home');
        },
        () => dispatch(modalClose()),
      ),
    );
  };

  const mapBundleItemsToCart = (product: LocalBundleEnvelopeWithChosenMainVariant): LocalProductVariant[] => {
    const items: LocalProductVariant[] = [];

    items.push({
      ...product.bundle.main_item.variants?.find(variant => variant.id === product.chosenMainItemVariantId),
      name: product.bundle.main_item.name,
    } as LocalProductVariant);

    const addons = product.bundle.items
      .map(addon =>
        addon.options.length
          ? ({
              ...addon.options[0],
              name: product?.addon_products?.find(addonItem => addonItem.id === addon.options[0].product_id)?.name,
            } as LocalProductVariant)
          : null,
      )
      .filter(addon => addon); // remove nulls or undefined

    items.push(...(addons as LocalProductVariant[]));
    return items;
  };

  const allowance = orderingForOrganization
    ? undefined
    : orderingOnBehalfUserData.status
      ? orderingOnBehalfUserData.userToOrderFor?.allowance
      : user?.allowance;
  const allowanceType = orderingForOrganization
    ? undefined
    : orderingOnBehalfUserData.status
      ? orderingOnBehalfUserData.userToOrderFor?.allowance_type
      : user?.allowance_type;

  return (
    product && (
      <>
        {isMobile && (
          <>
            <Gutter />
            <Divider />
            <Gutter />
          </>
        )}
        <ProductCart
          cartItems={mapBundleItemsToCart(product)}
          bundle={product}
          translations={productCartTranslations}
          showEmptyCartText={!orderResponse}
          handleEmptyCart={handleEmptyCartClick}
          allowance={allowance}
          allowanceType={allowanceType}
          locale={user?.organization?.locale.language}
        />
      </>
    )
  );
};

export default CheckoutProductCart;
